/* globals zoomSdk */
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Auth0User from "./Auth0User";
import Header from "./Header";
import IFrame from "./IFrame";
import Image from "./Image";
import UserInfo from "./UserInfo";

export const Authorization = (props) => {
  const {
    handleError,
    handleUser,
    handleUserContextStatus,
    user,
    userContextStatus,
    sdkConfigured,
  } = props;
  const location = useLocation();
  const [userAuthorized, setUserAuthorized] = useState(null);
  const [showInClientOAuthPrompt, setShowInClientOAuthPrompt] = useState(false);
  const [inGuestMode, setInGuestMode] = useState(false);
  
  // DataOffset processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiResults, setApiResults] = useState([]);

  // Centralized user data fetching function
  const fetchUserData = async () => {
    try {
      console.log("Fetching user data from Zoom API...");
      // An example of using the Zoom REST API via proxy
      const response = await fetch("/zoom/api/v2/users/me");
      
      // Handle missing OAuth tokens (401 Unauthorized)
      if (response.status === 401) {
        console.log('OAuth tokens missing - redirecting to install URL...');
        setShowInClientOAuthPrompt(true);
        // Redirect to the install URL which handles the complete OAuth flow
        const installUrl = 'https://auth.zoom.dataoffset.org/api/zoomapp/install';
        console.log('Opening install URL in browser:', installUrl);
        // Use Zoom SDK to open URL in browser (avoids CSP restrictions)
        await zoomSdk.openUrl({ url: installUrl });
        return;
      }
      
      if (response.status !== 200) {
        const errorText = await response.text();
        console.error("Zoom API error response:", errorText);
        throw new Error(`HTTP ${response.status}`);
      }
      const user = await response.json();
      console.log("Successfully fetched user data:", user);
      handleUser(user);
      setShowInClientOAuthPrompt(false);
      return user;
    } catch (error) {
      console.error("Error fetching user data:", error);
      console.log(
        "Request to Zoom REST API has failed ^, likely because no Zoom access token exists for this user. You must use the authorize API to get an access token"
      );
      setShowInClientOAuthPrompt(true);
      throw error;
    }
  };

  // Check DataOffset processing status
  const triggerDataOffsetProcessing = async () => {
    setIsProcessing(true);
    setApiResults([{ type: 'info', message: '🌱 Starting DataOffset carbon offset processing...' }]);

    try {
      console.log('Triggering DataOffset processing...');
      // Use absolute URL - in production this goes through Apache
      const apiUrl = `https://auth.zoom.dataoffset.org/api/dataoffset/process`;
      console.log('API URL:', apiUrl);

      // If user is missing, reset state and prompt re-auth
      if (!user || !user.email) {
        setApiResults(prev => [...prev, {
          type: 'error',
          message: '❌ No valid user credentials found. Please re-authenticate.'
        }]);
        setIsProcessing(false);
        setUserAuthorized(false);
        setShowInClientOAuthPrompt(true);
        return;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include', // Important: include cookies for session
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userProfile: user
        })
      });

      const responseText = await response.text();
      console.log('Response status:', response.status);
      console.log('Response text:', responseText);

      if (response.ok) {
        const result = JSON.parse(responseText);
        console.log('DataOffset processing result:', result);
        setApiResults(prev => [...prev, { 
          type: 'success', 
          message: '✅ DataOffset API calls triggered successfully',
          data: result 
        }]);
      } else {
        // If 401/403/500, reset state and prompt re-auth
        if ([401, 403, 500].includes(response.status)) {
          setApiResults(prev => [...prev, {
            type: 'error',
            message: `❌ DataOffset processing failed: ${response.status}`,
            details: responseText + '\nCredentials may be missing or expired. Please re-authenticate.'
          }]);
          setUserAuthorized(false);
          setShowInClientOAuthPrompt(true);
        } else {
          setApiResults(prev => [...prev, { 
            type: 'error', 
            message: `❌ DataOffset processing failed: ${response.status}`,
            details: responseText 
          }]);
        }
      }
    } catch (error) {
      console.error('Error triggering DataOffset processing:', error);
      setApiResults(prev => [...prev, { 
        type: 'error', 
        message: '❌ Error: ' + error.message 
      }]);
      setUserAuthorized(false);
      setShowInClientOAuthPrompt(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const promptAuthorize = async () => {
    try {
      const promptAuthResponse = await zoomSdk.promptAuthorize();
      console.log(promptAuthResponse);
    } catch (e) {
      console.error(e);
    }
  };

  const authorize = async () => {
    setShowInClientOAuthPrompt(false);
    console.log("Authorize flow begins here");
    console.log("1. Get code challenge and state from backend . . .");
    let authorizeResponse;
    try {
      authorizeResponse = await (await fetch("/api/zoomapp/authorize")).json();
      console.log(authorizeResponse);
      if (!authorizeResponse || !authorizeResponse.codeChallenge) {
        console.error(
          "Error in the authorize flow - likely an outdated user session.  Please refresh the app"
        );
        setShowInClientOAuthPrompt(true);
        return;
      }
    } catch (e) {
      console.error(e);
    }
    const { codeChallenge, state } = authorizeResponse;

    console.log("1a. Code challenge from backend: ", codeChallenge);
    console.log("1b. State from backend: ", state);

    const authorizeOptions = {
      codeChallenge: codeChallenge,
      state: state,
    };
    console.log("2. Invoke authorize, eg zoomSdk.authorize(authorizeOptions)");
    try {
      const zoomAuthResponse = await zoomSdk.authorize(authorizeOptions);
      console.log(zoomAuthResponse);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    // Only add event listeners after SDK is configured
    if (!sdkConfigured) return;
    
    // this is not the best way to make sure > 1 instances are not registered
    console.log("In-Client OAuth flow: onAuthorized event listener added");
    zoomSdk.addEventListener("onAuthorized", (event) => {
      const { code, state } = event;
      console.log("3. onAuthorized event fired.");
      console.log(
        "3a. Here is the event passed to event listener callback, with code and state: ",
        event
      );
      console.log(
        "4. POST the code, state to backend to exchange server-side for a token.  Refer to backend logs now . . ."
      );

      fetch("/api/zoomapp/onauthorized", {
        method: "POST",
        body: JSON.stringify({
          code,
          state,
          href: window.location.href,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      }).then(async () => {
        console.log(
          "4. Backend returns successfully after exchanging code for auth token.  Go ahead and update the UI"
        );
        setUserAuthorized(true);
        
        // Force re-fetch of user data after OAuth completion
        setTimeout(async () => {
          await fetchUserData();
        }, 1000);

        // the error === string
        handleError(null);
      });
    });
  }, [handleError, sdkConfigured]);

  useEffect(() => {
    // Only add event listeners after SDK is configured
    if (!sdkConfigured) return;
    
    zoomSdk.addEventListener("onMyUserContextChange", (event) => {
      handleUserContextStatus(event.status);
    });

    if (userContextStatus === "authorized") {
      setInGuestMode(false);
      fetchUserData().catch(err => {
        console.error("Failed to fetch user data:", err);
        setShowInClientOAuthPrompt(true);
      });
    } else if (
      userContextStatus === "unauthenticated" ||
      userContextStatus === "authenticated"
    ) {
      setInGuestMode(true);
    }
  }, [handleUser, handleUserContextStatus, userAuthorized, userContextStatus, sdkConfigured]);

  return (
    <>
      {!user && userContextStatus !== 'authorized' && (
        <div style={{ 
          padding: '40px 20px',
          textAlign: 'center',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <h2 style={{ marginBottom: '30px' }}>🌱 DataOffset Carbon Offset</h2>
          <p style={{ marginBottom: '30px', color: '#6c757d' }}>
            Click below to calculate your carbon footprint from Zoom meetings
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={inGuestMode ? promptAuthorize : authorize}
            style={{ padding: '15px 40px', fontSize: '18px' }}
          >
            Begin Calculation
          </Button>
        </div>
      )}

      {user && (
        <div style={{ 
          padding: '40px 20px',
          textAlign: 'center',
          minHeight: '100vh'
        }}>
          <h2 style={{ marginBottom: '10px' }}>
            Hello {user.first_name || 'User'} {user.last_name || ''}!
          </h2>
          <p style={{ color: '#6c757d', marginBottom: '40px' }}>
            {user.email || 'No email available'}
          </p>

          <Button
            variant="success"
            size="lg"
            onClick={triggerDataOffsetProcessing}
            disabled={isProcessing}
            style={{ padding: '15px 40px', fontSize: '18px', marginBottom: '30px' }}
          >
            {isProcessing ? 'Processing...' : '🌱 Calculate Carbon Footprint'}
          </Button>

          {/* API Results */}
          {apiResults.length > 0 && (
            <div style={{ 
              marginTop: '30px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              maxWidth: '800px',
              margin: '30px auto',
              textAlign: 'left'
            }}>
              <h4 style={{ marginBottom: '15px' }}>Processing Log:</h4>
              {apiResults.map((result, index) => (
                <div 
                  key={index} 
                  style={{ 
                    padding: '10px',
                    marginBottom: '10px',
                    backgroundColor: result.type === 'error' ? '#f8d7da' : 
                                   result.type === 'success' ? '#d4edda' : '#fff',
                    border: `1px solid ${result.type === 'error' ? '#f5c6cb' : 
                                        result.type === 'success' ? '#c3e6cb' : '#dee2e6'}`,
                    borderRadius: '4px'
                  }}
                >
                  <div>{result.message}</div>
                  {result.details && (
                    <pre style={{ 
                      fontSize: '12px', 
                      marginTop: '10px',
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '4px',
                      overflow: 'auto'
                    }}>
                      {typeof result.details === 'string' 
                        ? result.details 
                        : JSON.stringify(result.details, null, 2)}
                    </pre>
                  )}
                  {result.data && (
                    <pre style={{ 
                      fontSize: '12px', 
                      marginTop: '10px',
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '4px',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
              
              {/* Dashboard Links */}
              <div style={{ 
                marginTop: '30px',
                padding: '20px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <h5 style={{ marginBottom: '15px' }}>Access Dashboard:</h5>
                <div style={{ marginBottom: '10px' }}>
                  <a 
                    href="https://zoom.dataoffset.org/dashboard/"
                    style={{ 
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#007bff',
                      color: '#fff',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      marginRight: '10px'
                    }}
                  >
                    📊 Dashboard (No Auth)
                  </a>
                </div>
                {user && user.id && (
                  <>
                    <div>
                      <a 
                        href={`https://zoom.dataoffset.org/dashboard/?OCSESSID=${user.id}`}
                        style={{ 
                          display: 'inline-block',
                          padding: '10px 20px',
                          backgroundColor: '#28a745',
                          color: '#fff',
                          textDecoration: 'none',
                          borderRadius: '4px'
                        }}
                      >
                        🔐 Dashboard (With Auth)
                      </a>
                    </div>
                    <div style={{ 
                      marginTop: '10px',
                      fontSize: '12px',
                      color: '#6c757d'
                    }}>
                      Your Zoom UUID: <code>{user.id}</code>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
